// Microbe RTS — right-edge minimap ribbon.
//
// The map is TALL (surface at top, abyss at bottom) so the minimap is a vertical strip pinned to
// the right edge: world y maps straight down the ribbon, world x across its narrow width. Nodes are
// owner-colored AND owner-shaped (colorblind-safe: circle=you, triangle=bot/enemy, square=neutral),
// enemy nodes pulse. A white box shows the live camera viewport. A tap inside the ribbon converts to
// a world point (minimapHitToWorld) so the UI can jump the camera there.
//
// Pure draw. All layout numbers here are screen-space visual constants; the only world numbers are
// the map dimensions from config.WORLD.

import { WORLD } from '../config.js';
import type { GameState, Side, Vec } from '../types.js';
import type { Camera } from './camera.js';
import type { Fog } from './fog.js';

/** Ribbon rectangle in CSS px (element-local). */
export interface MinimapRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const COL_YOU = '#4fd1b0';
const COL_BOT = '#ff6a5a';
const COL_WILD = '#d0a83f';
const COL_NEUTRAL = '#8a97a3';
const COL_FRAME = 'rgba(255,255,255,0.85)';

function miniX(rect: MinimapRect, worldX: number): number {
  return rect.x + (worldX / WORLD.width) * rect.w;
}
function miniY(rect: MinimapRect, worldY: number): number {
  return rect.y + (worldY / WORLD.height) * rect.h;
}

/**
 * Draw the ribbon. `cam` drives the viewport box. Returns the rect it drew into so a caller can
 * feed the same rect to minimapHitToWorld.
 */
export function drawMinimap(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  cam: Camera,
  rect: MinimapRect,
  fog?: Fog,
  viewSide: Side = 'you',
): MinimapRect {
  // The LOCAL player's side (solo 'you'; PvP the session's side) — own assets show in COL_YOU,
  // the opponent in COL_BOT. Defaults to 'you' so solo is unchanged.
  const foeSide: Side = viewSide === 'you' ? 'bot' : 'you';
  const pulse = 0.55 + 0.45 * Math.sin(performance.now() / 1000 * 4);

  // ---- ribbon backdrop: lit surface (top) → dark abyss (bottom) ----
  const bg = ctx.createLinearGradient(0, rect.y, 0, rect.y + rect.h);
  bg.addColorStop(0, 'rgba(40,90,110,0.85)');
  bg.addColorStop(0.55, 'rgba(10,26,38,0.85)');
  bg.addColorStop(1, 'rgba(24,8,32,0.85)');
  ctx.fillStyle = bg;
  ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 1;
  ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1);

  // ---- nodes: shape by ownership, color by ownership, enemy pulses ----
  // Terrain is remembered once seen: your nodes always show; others only after you've EXPLORED
  // them (so the fog hides the enemy home + unscouted craters, but you don't forget a node you found).
  for (const n of state.nodes) {
    if (n.owner !== viewSide && fog && !fog.isExplored(n.pos)) continue;
    const mx = miniX(rect, n.pos.x);
    const my = miniY(rect, n.pos.y);
    const isHome = n.type === 'home';
    const s = isHome ? 4.2 : 3;
    if (n.owner === viewSide) {
      ctx.fillStyle = COL_YOU;
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(mx, my, s, 0, Math.PI * 2);
      ctx.fill();
    } else if (n.owner === foeSide) {
      ctx.fillStyle = COL_BOT;
      ctx.globalAlpha = 0.4 + 0.6 * pulse;
      ctx.beginPath(); // down triangle
      ctx.moveTo(mx, my + s);
      ctx.lineTo(mx - s, my - s);
      ctx.lineTo(mx + s, my - s);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    } else {
      ctx.fillStyle = COL_NEUTRAL;
      ctx.globalAlpha = 1;
      ctx.fillRect(mx - s, my - s, s * 2, s * 2);
    }
    if (isHome) {
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(mx, my, s + 2, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;

  // ---- live units: 1px owner-colored dots. YOUR units always; rival + wild only where you can
  //      currently SEE them (fog of war). ----
  for (const u of state.units) {
    if (u.side !== viewSide) {
      if (fog && !fog.isVisible(u.pos)) continue;
      ctx.fillStyle = u.side === 'wild' ? COL_WILD : COL_BOT;
    } else {
      ctx.fillStyle = COL_YOU;
    }
    ctx.fillRect(miniX(rect, u.pos.x) | 0, miniY(rect, u.pos.y) | 0, 1, 1);
  }

  // ---- fog veil over the ribbon: unexplored = dark unknown, explored = dimmed, in-sight = clear.
  //      Drawn ABOVE the terrain/markers so the three states read distinctly; the camera frame
  //      below is drawn AFTER so it stays visible even over unexplored water. ----
  if (fog) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h); // clip so the scaled veil texture can't bleed past the ribbon
    ctx.clip();
    fog.drawVeil(ctx, rect.x, rect.y, rect.w, rect.h);
    ctx.restore();
  }

  // ---- wild lairs: once you've SCOUTED a grazer's territory, its spot stays marked here for the
  //      rest of the match (a hollow amber diamond), even after your units leave and it's out of
  //      live sight. Drawn ABOVE the veil so remembered intel is always legible. ----
  if (fog) {
    ctx.strokeStyle = COL_WILD;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 1;
    const s = 3;
    for (const lair of state.lairs) {
      if (!fog.isExplored(lair.pos)) continue;
      const mx = miniX(rect, lair.pos.x);
      const my = miniY(rect, lair.pos.y);
      ctx.beginPath();
      ctx.moveTo(mx, my - s);
      ctx.lineTo(mx + s, my);
      ctx.lineTo(mx, my + s);
      ctx.lineTo(mx - s, my);
      ctx.closePath();
      ctx.stroke();
    }
  }

  // ---- camera viewport box (project the two screen corners into world, then onto the ribbon) ----
  const tl = cam.screenToWorld({ x: 0, y: 0 });
  const br = cam.screenToWorld({ x: cam.viewW, y: cam.viewH });
  const bx = miniX(rect, Math.max(0, Math.min(WORLD.width, tl.x)));
  const by = miniY(rect, Math.max(0, Math.min(WORLD.height, tl.y)));
  const bx2 = miniX(rect, Math.max(0, Math.min(WORLD.width, br.x)));
  const by2 = miniY(rect, Math.max(0, Math.min(WORLD.height, br.y)));
  ctx.strokeStyle = COL_FRAME;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(bx, by, Math.max(2, bx2 - bx), Math.max(2, by2 - by));

  return rect;
}

/**
 * Convert a tap (CSS px, element-local) inside the ribbon into a world point, or null if outside.
 * The UI uses this to jump the camera. Clamped to the ribbon so edge taps still resolve on-map.
 */
export function minimapHitToWorld(rect: MinimapRect, p: Vec): Vec | null {
  if (p.x < rect.x || p.x > rect.x + rect.w || p.y < rect.y || p.y > rect.y + rect.h) return null;
  const fx = (p.x - rect.x) / rect.w;
  const fy = (p.y - rect.y) / rect.h;
  return { x: fx * WORLD.width, y: fy * WORLD.height };
}
